import { Box, Button, Popper, ClickAwayListener, Paper, Typography, TextField, MenuItem, useTheme, MenuList } from "@mui/material"
import FilterAltIcon from "@mui/icons-material/FilterAlt"
import { useSearchParams } from 'react-router-dom'
import React, { useState, useRef } from "react"
import SortIcon from "@mui/icons-material/Sort"
import { debounce, Icons } from '../../../utils/utilities'

// import { Icons } from '../../../utils/icons.jsx'
// import { debounce } from '../../../utils/helpers.jsx'

const filterOperations = [
    { label: "contains", value: "contains" },
    { label: "equals", value: "eq" },
    { label: "starts with", value: "startswith" },
    { label: "ends with", value: "endswith" },
    { label: "not equal", value: "neq" },
    { label: "Greater than", value: "gt" },
    { label: "Less than", value: "lt" },
]
const sortOptions = [
    { label: "ASC", value: "asc" },
    { label: "DESC", value: "desc" }
]

const filterConditions = [
    "and", "or"
]


const encodeSort = ( { field, order } ) => `field:${field},order:${order}`
const encodeFilter = ( { field, operator, value } ) => `field:${field},op:${operator},value:${value}`

const SortAndFilterBar = ( {
    tableKey,
    removeFilters,
    sortOpen = false, setSortOpen,
    filterOpen = false, setFilterOpen,
    sortables = [],
    filterables = [],
    onSortChange,
    onFilterChange,
} ) => {

    const [sortOptionsAnchorEl, setSortOptionsAnchorEl] = useState( null )
    const [filterOptionsAnchorEl, setFilterOptionsAnchorEl] = useState( null )
    const [availableSortsAnchorEl, setAvailableSortsAnchorEl] = useState( null )
    const [availableFilterAnchorEl, setAvailableFilterAnchorEl] = useState( null )
    const [filterConditionsAnchorEl, setFilterConditionsAnchorEl] = useState( null )

    // --- Filter Popper ---
    const sortAnchorRef = useRef( null )
    const filterAnchorRef = useRef( null )
    const dragIndexRef = useRef( null )
    const filterValueChangeDebouncer = useRef( {} )

    const { border, palette, layout, zIndex } = useTheme()

    const [searchParams, setSearchParams] = useSearchParams( { filtercondition: 'and' } )

    const filtercondition = searchParams.get( 'filtercondition' ) || 'and'

    const sorts = searchParams.getAll( "sort" ).filter( item => tableKey ? item.startsWith( tableKey ) : true ).map( item => {
        let sortDetails = []
        if ( tableKey ) {
            sortDetails = item.split( `${tableKey}:` ).at( 1 ).split( "," ).map( p => p.split( ":" ) )
        } else {
            sortDetails = item.split( "," ).map( p => p.split( ":" ) )
        }
        const obj = Object.fromEntries( sortDetails )
        return { field: obj.field, order: obj.order }
    } )

    const filters = searchParams.getAll( "filter" ).filter( item => tableKey ? item.startsWith( tableKey ) : true ).map( item => {
        let filterDetails = []
        if ( tableKey ) {
            filterDetails = item.split( `${tableKey}:` ).at( 1 ).split( "," ).map( p => p.split( ":" ) )
        } else {
            filterDetails = item.split( "," ).map( p => p.split( ":" ) )
        }
        const obj = Object.fromEntries( filterDetails )

        return { field: obj.field, operator: obj.op, value: obj.value }
    } )

    const availableSorts = sortables.filter( so => !Boolean( sorts.find( srt => srt.field === so.field ) ) )
    const availableFilterOptions = filterables.filter( fo => !Boolean( filters.find( filter => filter.field === fo.field ) ) )

    const toggleSort = () => setSortOpen( prev => !prev )
    const toggleFilter = () => setFilterOpen( prev => !prev )

    const closeSort = () => setSortOpen( false )
    const closeFilter = () => setFilterOpen( false )

    const addFilter = () => {
        const newFilter = availableFilterOptions.at( 0 )
        onFilterChange( "set", { field: newFilter.field, value: "", operator: "contains" } )
    }

    const updateSort = ( index, update ) => {
        onSortChange( "update", { index, update } )
        setAvailableSortsAnchorEl( null )
    }

    const updateFilter = ( index, update ) => {
        onFilterChange( "update", { index, update } )
        setAvailableFilterAnchorEl( null )
    }

    const updateFilterCondition = ( condition ) => {
        setSearchParams( ( prev ) => {
            const params = new URLSearchParams( prev )
            params.set( 'filtercondition', condition )
            return params
        }, { replace: true } )
    }


    const addNewSort = () => {
        onSortChange?.( "set", { field: availableSorts.at( 0 ).field, order: "asc" } )
    }

    const filterValueDebouncer = ( filterIndex, value ) => onFilterChange( "update", { index: filterIndex, update: { value } } )

    const handleFilterValueChange = ( filterIndex, value ) => {
        if ( !filterValueChangeDebouncer.current[filterIndex] )
            filterValueChangeDebouncer.current[filterIndex] = debounce( `${filterIndex}-filtervalue-change`, filterValueDebouncer, 1000 )
        filterValueChangeDebouncer.current[filterIndex]?.( filterIndex, value )
    }

    const handleSortDragStart = ( e, index ) => {
        dragIndexRef.current = index
        e.dataTransfer.effectAllowed = "move"
        try {
            // some browsers require setting data
            e.dataTransfer.setData( "text/plain", index )
        } catch ( err ) {
            // ignore (IE)
        }
    }


    const handleSortDragOver = ( e, overIndex ) => {
        e.preventDefault() // allow drop


        const dragIndex = dragIndexRef.current
        if ( dragIndex === null || dragIndex === overIndex ) return

        const newSorts = [...sorts]
        const [moved] = newSorts.splice( dragIndex, 1 )
        newSorts.splice( overIndex, 0, moved )
        dragIndexRef.current = overIndex // update reference
        setSearchParams( prev => {
            const params = new URLSearchParams( prev )
            params.delete( "sort" ) // clear old entries
            newSorts.forEach( s => params.append( "sort", encodeSort( s ) ) )
            return params
        }, { replace: true } )
    }


    const handleSortDragEnd = () => {
        dragIndexRef.current = null
    }


    const handleFilterDragStart = ( e, index ) => {
        dragIndexRef.current = index
        e.dataTransfer.effectAllowed = "move"
        try {
            // some browsers require setting data
            e.dataTransfer.setData( "text/plain", index )
        } catch ( err ) {
            // ignore (IE)
        }
    }


    const handleFilterDragOver = ( e, overIndex ) => {
        e.preventDefault() // allow drop


        const dragIndex = dragIndexRef.current
        if ( dragIndex === null || dragIndex === overIndex ) return

        const newFilters = [...filters]
        const [moved] = newFilters.splice( dragIndex, 1 )
        newFilters.splice( overIndex, 0, moved )
        dragIndexRef.current = overIndex // update reference
        setSearchParams( prev => {
            const params = new URLSearchParams( prev )
            params.delete( "filter" ) // clear old entries
            newFilters.forEach( s => params.append( "filter", encodeFilter( s ) ) )
            return params
        }, { replace: true } )
    }


    const handleFilterDragEnd = () => {
        dragIndexRef.current = null
    }



    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "flex-start",
                gap: layout.cardsGap
            }}>
            {/* Sort Button */}
            {sortables.length > 0 && <Button size="small" variant="outlined" color="greyed" ref={sortAnchorRef} onClick={toggleSort} startIcon={<SortIcon />} >
                Sort
            </Button>}
            {/* Filter Button */}
            {filterables.length > 0 && <Button size="small" variant="outlined" color="greyed" ref={filterAnchorRef} onClick={toggleFilter} startIcon={<FilterAltIcon />} >
                Filter
            </Button>}
            {/* POPPERS */}
            {/* Sort Popper */}
            <Popper sx={{ zIndex: zIndex.tooltip + 10 }} open={sortOpen} anchorEl={sortAnchorRef.current} placement="bottom-start" >
                <ClickAwayListener onClickAway={closeSort}>
                    <Paper sx={{ maxWidth: "90vw", padding: "20px", minWidth: { xs: "initial", md: "200px" }, border: border[1], marginTop: "5px" }}>
                        {sorts.length > 0 && <Box>
                            <Typography variant='subtitle2' sx={{
                                display: "block"
                            }}>
                                Sort By
                            </Typography>
                            <Box
                                sx={{
                                    marginTop: "10px",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "10px"
                                }}>
                                {sorts.map( ( sort, sortIndex ) => {
                                    const colDetails = sortables.find( so => so.field === sort.field )
                                    // if ( !colDetails )
                                    //     return null

                                    return (
                                        <Box
                                            component="div"
                                            draggable
                                            onDragStart={( e ) => handleSortDragStart( e, sortIndex )}
                                            onDragOver={( e ) => handleSortDragOver( e, sortIndex )}
                                            onDragEnd={handleSortDragEnd}
                                            key={sort.field}
                                            sx={{
                                                position: "relative",
                                                display: "flex",
                                                gap: "10px",
                                                overflow: "clip",
                                                flexWrap: "wrap"
                                            }}>
                                            <Button onClick={( event ) => setAvailableSortsAnchorEl( availableSorts.length > 0 ? event.currentTarget : null )} sx={{ width: "200px", justifyContent: "space-between", fontWeight: "normal" }} size='small' variant='outlined' color='greyed' endIcon={Icons.default.UnfoldMore} >
                                                {colDetails.label || colDetails.name}
                                            </Button>
                                            <Button onClick={( event ) => setSortOptionsAnchorEl( event.currentTarget )} sx={{ width: "80px", justifyContent: "space-between", fontWeight: "normal", textTransform: "uppercase" }} size='small' variant='outlined' color='greyed' endIcon={sort.order !== "asc" ? Icons.default.KeyboardArrowDownIcon : Icons.default.KeyboardArrowUp} >
                                                {sort.order}
                                            </Button>
                                            <Button onClick={() => onSortChange( "remove", { field: sort.field } )} size='small' color='greyed' variant='outlined' sx={{ color: palette.common.font, fontSize: "18px", minWidth: "0" }} >
                                                {Icons.default.DeleteIconOutlined}
                                            </Button>
                                            <Button size='small' color='greyed' variant='outlined' sx={{ color: palette.common.font, fontSize: "18px", minWidth: "0", cursor: "grab" }} >
                                                {Icons.default.DragIndicatorIcon}
                                            </Button>
                                            <Popper sx={{ zIndex: zIndex.tooltip + 10 }} open={Boolean( availableSortsAnchorEl )} anchorEl={availableSortsAnchorEl} placement='bottom-start' >
                                                <ClickAwayListener onClickAway={() => setAvailableSortsAnchorEl( null )}>
                                                    <Paper sx={{ minWidth: 200, border: border[1], marginTop: "5px" }}>
                                                        <MenuList>
                                                            {availableSorts.map( srt => (
                                                                <MenuItem sx={{ fontSize: "14px" }} onClick={() => updateSort( sortIndex, { field: srt.field } )} key={srt.field} >{srt.name || srt.label || srt.field}</MenuItem>
                                                            ) )}
                                                        </MenuList>
                                                    </Paper>
                                                </ClickAwayListener>
                                            </Popper>
                                            <Popper sx={{ zIndex: zIndex.tooltip + 10 }} open={Boolean( sortOptionsAnchorEl )} anchorEl={sortOptionsAnchorEl} placement='bottom-start' >
                                                <ClickAwayListener onClickAway={() => setSortOptionsAnchorEl( null )}>
                                                    <Paper sx={{ minWidth: 200, border: border[1], marginTop: "5px" }}>
                                                        <MenuList>
                                                            {sortOptions.map( srtOp => (
                                                                <MenuItem selected={srtOp.value === sort.order} sx={{ fontSize: "14px" }} onClick={() => updateSort( sortIndex, { order: srtOp.value } )} key={srtOp.value} >{srtOp.label}</MenuItem>
                                                            ) )}
                                                        </MenuList>
                                                    </Paper>
                                                </ClickAwayListener>
                                            </Popper>
                                        </Box>
                                    )
                                } )}



                            </Box>
                        </Box>}
                        {sorts.length === 0 && <Box>
                            <Typography variant='subtitle2' sx={{
                                display: "block"
                            }}> No sorting applied </Typography>
                            <Typography
                                color='textSecondary'
                                variant='subtitle2'
                                sx={{
                                    fontSize: "12px",
                                    display: "block"
                                }}>Add sorting to organize your rows.</Typography>
                        </Box>}
                        {availableSorts.length > 0 && <Button variant='contained' disableElevation size="small" onClick={addNewSort} sx={{ marginTop: "10px" }} >Add sort</Button>}
                    </Paper>
                </ClickAwayListener>
            </Popper>
            {/* Filter Popper */}
            <Popper sx={{ zIndex: zIndex.tooltip + 10 }} open={filterOpen} anchorEl={filterAnchorRef.current} placement="bottom-start" >
                <ClickAwayListener onClickAway={closeFilter}>
                    <Paper
                        sx={{ maxWidth: "90vw", padding: "20px", minWidth: { xs: "initial", md: "550px" }, border: border[1], marginTop: "5px", bgcolor: palette.background.paper }}
                    >

                        {filters.length > 0 && <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "10px"
                            }}>
                            <Typography variant='subtitle2' sx={{
                                display: "block"
                            }}>
                                Filters
                            </Typography>
                            {filters.map( ( filter, filterIndex ) => {
                                const colDetails = filterables.find( fo => fo.field === filter.field )
                                const operation = filterOperations.find( fo => fo.value === filter.operator )

                                if ( !colDetails ) return null

                                return (
                                    <Box
                                        component="div"
                                        draggable
                                        onDragStart={( e ) => handleFilterDragStart( e, filterIndex )}
                                        onDragOver={( e ) => handleFilterDragOver( e, filterIndex )}
                                        onDragEnd={handleFilterDragEnd}
                                        key={filter.field}
                                        sx={{
                                            position: "relative",
                                            display: "flex",
                                            gap: "10px",
                                            flexWrap: "wrap"
                                        }}>
                                        {![1].includes( filterIndex ) && <Typography
                                            component="div"
                                            sx={{
                                                justifyContent: "flex-end",
                                                width: "70px",
                                                height: "max-height",
                                                display: "flex",
                                                alignItems: "center",
                                                fontSize: "12px",
                                                color: "text.secondary"
                                            }}>
                                            {filterIndex === 0 ? "Where" : ""}
                                        </Typography>}
                                        {filterIndex === 1 && <Button onClick={( event ) => setFilterConditionsAnchorEl( event.currentTarget )} sx={{ width: "70px", justifyContent: "space-between", fontWeight: "normal", textTransform: "uppercase" }} size='small' variant='outlined' color='greyed' endIcon={Icons.default.KeyboardArrowDownIcon} >
                                            {filtercondition}
                                        </Button>}
                                        <Button onClick={( event ) => setAvailableFilterAnchorEl( availableFilterOptions.length > 0 ? event.currentTarget : null )} sx={{ width: "100px", justifyContent: "space-between", fontWeight: "normal" }} size='small' variant='outlined' color='greyed' endIcon={Icons.default.UnfoldMore} >
                                            <Typography sx={{ fontSize: "inherit" }} noWrap >{colDetails.label || colDetails.name} </Typography>
                                        </Button>
                                        <Button onClick={( event ) => setFilterOptionsAnchorEl( event.currentTarget )} sx={{ width: "120px", justifyContent: "space-between", fontWeight: "normal" }} size='small' variant='outlined' color='greyed' endIcon={Icons.default.KeyboardArrowDownIcon} >
                                            {operation.label}
                                        </Button>
                                        <TextField color='greyed' onChange={( event ) => handleFilterValueChange( filterIndex, event.target.value )} defaultValue={filter.value} sx={{ borderRadius: layout.borderRadius }} slotProps={{ input: { sx: { borderRadius: layout.borderRadius } }, htmlInput: { sx: { fontSize: "13px", padding: "6px", } } }} size='small' variant='outlined' placeholder='Value' />
                                        <Button onClick={() => onFilterChange( "remove", { field: filter.field } )} size='small' color='greyed' variant='outlined' sx={{ color: palette.common.font, fontSize: "18px", minWidth: "0" }} >
                                            {Icons.default.DeleteIconOutlined}
                                        </Button>
                                        <Button size='small' color='greyed' variant='outlined' sx={{ color: palette.common.font, fontSize: "18px", minWidth: "0", cursor: "grab" }} >
                                            {Icons.default.DragIndicatorIcon}
                                        </Button>
                                        <Popper sx={{ zIndex: zIndex.tooltip + 10 }} open={Boolean( availableFilterAnchorEl )} anchorEl={availableFilterAnchorEl} placement='bottom-start' >
                                            <ClickAwayListener onClickAway={() => setAvailableFilterAnchorEl( null )}>
                                                <Paper sx={{ minWidth: 200, border: border[1], marginTop: "5px", borderRadius: layout.borderRadius }}>
                                                    <MenuList>
                                                        {availableFilterOptions.map( filter => (
                                                            <MenuItem sx={{ fontSize: "14px", textTransform: "capitalize", borderRadius: layout.borderRadius }} onClick={() => updateFilter( filterIndex, { field: filter.field } )} key={filter.field} >{filter.name || filter.label || filter.field}</MenuItem>
                                                        ) )}
                                                    </MenuList>
                                                </Paper>
                                            </ClickAwayListener>
                                        </Popper>
                                        <Popper sx={{ zIndex: zIndex.tooltip + 10 }} open={Boolean( filterOptionsAnchorEl )} anchorEl={filterOptionsAnchorEl} placement='bottom-start' >
                                            <ClickAwayListener onClickAway={() => setFilterOptionsAnchorEl( null )}>
                                                <Paper sx={{ minWidth: 200, border: border[1], marginTop: "5px", borderRadius: layout.borderRadius }}>
                                                    <MenuList>
                                                        {filterOperations.map( filterOp => (
                                                            <MenuItem selected={filterOp.value === filter.operator} sx={{ fontSize: "14px", textTransform: "capitalize", borderRadius: layout.borderRadius }} onClick={() => updateFilter( filterIndex, { operator: filterOp.value } )} key={filterOp.value} >{filterOp.label}</MenuItem>
                                                        ) )}
                                                    </MenuList>
                                                </Paper>
                                            </ClickAwayListener>
                                        </Popper>
                                        <Popper sx={{ zIndex: zIndex.tooltip + 10 }} open={Boolean( filterConditionsAnchorEl )} anchorEl={filterConditionsAnchorEl} placement='bottom-start' >
                                            <ClickAwayListener onClickAway={() => setFilterConditionsAnchorEl( null )}>
                                                <Paper sx={{ minWidth: 200, border: border[1], marginTop: "5px" }}>
                                                    <MenuList>
                                                        {filterConditions.map( filterCond => (
                                                            <MenuItem selected={filterCond === filtercondition} sx={{ fontSize: "14px", textTransform: "uppercase" }} onClick={() => updateFilterCondition( filterCond )} key={filterCond} >{filterCond}</MenuItem>
                                                        ) )}
                                                    </MenuList>
                                                </Paper>
                                            </ClickAwayListener>
                                        </Popper>
                                    </Box>
                                )
                            } )}
                        </Box>}
                        {filters.length === 0 && <Box>
                            <Typography variant='subtitle2' sx={{
                                display: "block"
                            }}> No filters applied </Typography>
                            <Typography
                                color='textSecondary'
                                variant='subtitle2'
                                sx={{
                                    fontSize: "12px",
                                    display: "block"
                                }}>Add filters to refine your rows.</Typography>
                        </Box>}

                        <Box
                            sx={{
                                mt: 2,
                                display: "flex",
                                gap: 1
                            }}>
                            {availableFilterOptions.length > 0 && <Button disableElevation size="small" variant="contained" onClick={addFilter} >
                                Add filter
                            </Button>}

                            {filters.length > 0 && <Button size="small" color='error' onClick={() => { removeFilters(); setFilterOpen( false ) }} variant="outlined" >
                                Reset filters
                            </Button>}
                        </Box>
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </Box >
    )
}

export default SortAndFilterBar
